import React from 'react';
import jp from 'jsonpath';
import numeral from 'numeral';
import { Box, colors, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import {
  DataGridPro,
  gridClasses,
  GridToolbarContainer,
  GridCellParams,
  useGridApiRef,
  GridSlotsComponentsProps,
} from '@mui/x-data-grid-pro';
import { LicenseInfo } from '@mui/x-license';

import useTranslations from '../../../hooks/useTranslations';
import contentHelper from 'helpers/contentHelper';
import errorHelper from 'helpers/errorHelper';
import { neutral } from '_lex/_foundations/phxColors';

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_KEY ?? '');

export interface ColumnDef {
  [key: string]: unknown;
}

export type FieldActions = {
  [key: string]: (params: GridCellParams) => void;
};

export type GridSectionProps = {
  columns?: ColumnDef;
  data: any[];
  gridProps?: any;
  query?: {
    isLoading?: boolean;
    error?: any;
    data?: any;
    isFetchingNextPage?: boolean;
    hasNextPage?: boolean;
    fetchNextPage?: () => Promise<any>;
    isError?: boolean;
  };
  totalCount?: number;
  isLoading?: boolean;
  rowAction?: (data: any) => void;
  sorting?: any;
  onSortModelChange?: (model: any) => void;
  gridKey?: string;
};

const GridSection: React.FC<GridSectionProps> = ({
  columns = {},
  data = [],
  gridProps,
  query,
  totalCount,
  isLoading = false,
  rowAction = () => {},
  sorting,
  onSortModelChange,
  gridKey: propGridKey,
}: GridSectionProps) => {
  const gridApiRef = useGridApiRef();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fieldActions: FieldActions = {};

  React.useEffect(() => {
    const rowCount = query?.data?.pages?.[0]?.count ?? totalCount;

    if (gridApiRef.current?.setRowCount && rowCount !== undefined) {
      gridApiRef.current.setRowCount(rowCount);
    }
  }, [query?.data, totalCount, gridApiRef]);

  const CustomGridToolbar = () => (
    <GridToolbarContainer
      sx={{
        p: 2,
        borderBottom: `solid 1px ${colors.grey[200]}`,
        display: 'flex',
        justifyContent: 'end',
      }}
    ></GridToolbarContainer>
  );

  const getValueByPath = (rowData: any, path: string) => {
    let val;
    if (rowData) {
      const cellVal = jp.value(rowData, path);
      if (cellVal) val = cellVal;
    }
    return val;
  };

  const getFormatter = (options: any) => {
    if (options.formatter) return options.formatter;
    if (options.type === 'number') return (value: any) => value ?? 0;
    return null;
  };

  const getRenderer = (options: any) => {
    if (options.renderer) return options.renderer;
    if (options.type === 'number')
      return (params: GridCellParams) => (Number(params.value) >= 0 ? numeral(params.value).format('0,0') : '--');
    return null;
  };

  const columnDefs = Object.entries(columns).map(([field, options]) => {
    const columnOptions = options as any;
    if (columnOptions.onClick) fieldActions[field] = columnOptions.onClick;
    return {
      field,
      name: field,
      headerName: columnOptions.header ?? contentHelper.humanize(field),
      valueGetter: columnOptions.valueGetter
        ? (value: any, row: any) => columnOptions.valueGetter(value, row)
        : (value: any, row: any) => getValueByPath(row, field),
      type: columnOptions.type ?? 'string',
      valueOptions: columnOptions.valueOptions,
      cellClassName: columnOptions.cellClassName ?? (columnOptions.onClick ? 'phx-link-cell' : ''),
      align: columnOptions.align ?? 'left',
      headerAlign: columnOptions.align ?? 'left',
      flex: columnOptions.flex,
      minWidth: columnOptions.minWidth ?? 100,
      sortable: sorting !== null && sorting !== undefined && columnOptions.sortable !== false,
      filterable: false,
      ...(getFormatter(options)
        ? {
            valueFormatter: (value: any) => {
              const func = getFormatter(options);
              return func(value);
            },
          }
        : {}),
      ...(getRenderer(options)
        ? {
            renderCell: (params: any) => {
              const func = getRenderer(options);
              return func(params);
            },
          }
        : {}),
      ...(columnOptions.columnProps || {}),
    };
  });

  const handleCellClick = (params: GridCellParams) => {
    if (fieldActions[params.field]) {
      fieldActions[params.field](params);
    }
  };

  function CustomFooter(props: NonNullable<GridSlotsComponentsProps['footer']>) {
    const currentCount = data.length || 0;
    const total = query?.data?.pages?.[0]?.count || totalCount || 0;

    return (
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'end',
          gap: 3,
          height: 48,
          alignItems: 'center',
          borderTop: `solid 1px ${neutral[200]}`,
        }}
      >
        {query?.isFetchingNextPage && <CircularProgress size={36} />}
        {numeral(currentCount).format('0,0')} of {numeral(total).format('0,0')}
      </Box>
    );
  }

  const handleRowsScrollEnd = () => {
    if (query?.hasNextPage && !query.isFetchingNextPage && !query.isLoading && query.fetchNextPage !== undefined) {
      setTimeout(() => {
        if (query.fetchNextPage) {
          query
            .fetchNextPage()
            .then(() => {
              gridApiRef.current?.autosizeColumns();
            })
            .catch((error: any) => {
              console.error('Error fetching next page:', error);
            });
        }
      }, 100);
    }
  };

  // console.log('isLoading', isLoading);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateRows: '1fr',
        height: '100%',
        minHeight: 0,
        '& .MuiDataGrid-row.Mui-disabled .MuiCheckbox-root': {
          pointerEvents: 'none',
          opacity: 0.5,
        },
        '& .MuiDataGrid-cell': {
          borderColor: neutral[200],
          display: 'flex',
          alignItems: 'center',
        },
        '& .MuiDataGrid-columnHeaders': {
          borderColor: neutral[200],
          backgroundColor: neutral[50],
        },
        [`.${gridClasses.cell}.phx-link-cell`]: {
          color: '#004B87',
          fontWeight: 600,
          cursor: 'pointer',
        },
      }}
    >
      <Box
        sx={{
          minHeight: 0,
          overflow: 'auto',
          height: '100%',
        }}
      >
        {query?.isError ? (
          errorHelper.renderErrors(query)
        ) : (
          <DataGridPro
            apiRef={gridApiRef}
            rows={data}
            columns={columnDefs}
            onCellClick={handleCellClick}
            rowSelectionModel={[]}
            loading={isLoading || (query?.isLoading && !query.isFetchingNextPage)}
            slots={{
              toolbar: CustomGridToolbar,
              footer: CustomFooter,
            }}
            initialState={{
              density: 'comfortable',
            }}
            localeText={{ toolbarExport: '' }}
            hideFooterPagination
            onRowsScrollEnd={handleRowsScrollEnd}
            rowHeight={64}
            columnHeaderHeight={40}
            sortingOrder={sorting !== null && sorting !== undefined ? ['asc', 'desc'] : undefined}
            sortModel={sorting}
            onSortModelChange={sorting !== null && sorting !== undefined ? onSortModelChange : undefined}
            disableColumnFilter
            {...gridProps}
            autoHeight={false}
            key={propGridKey}
          />
        )}
      </Box>
    </Box>
  );
};

export default GridSection;
